import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import IgStoryShare from '@/pages/daily-wisdom/components/IgStoryShare';
import { wisdomCards, problemCategories, type WisdomCard } from '@/mocks/wisdom';

function getTodayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function loadDrawnIds(): { date: string; ids: number[] } {
  try {
    const raw = localStorage.getItem('treehug_wisdom_v3');
    if (!raw) return { date: '', ids: [] };
    const parsed = JSON.parse(raw);
    return { date: parsed.date || '', ids: Array.isArray(parsed.ids) ? parsed.ids : [] };
  } catch {
    return { date: '', ids: [] };
  }
}

function saveDrawnIds(date: string, ids: number[]) {
  try {
    localStorage.setItem('treehug_wisdom_v3', JSON.stringify({ date, ids }));
  } catch {
    // ignore
  }
}

/* Elegant inline SVG line-art for each category */
const categoryArts: Record<string, React.ReactNode> = {
  career: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="18" width="24" height="14" rx="2" stroke="currentColor" strokeWidth="1.2" />
      <line x1="14" y1="18" x2="14" y2="14" stroke="currentColor" strokeWidth="1" />
      <line x1="26" y1="18" x2="26" y2="14" stroke="currentColor" strokeWidth="1" />
      <line x1="12" y1="14" x2="28" y2="14" stroke="currentColor" strokeWidth="1" />
      <line x1="16" y1="24" x2="24" y2="24" stroke="currentColor" strokeWidth="1" />
    </svg>
  ),
  parenting: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="14" r="5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M12 34 Q12 24 20 24 Q28 24 28 34" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <circle cx="17" cy="13" r="1" fill="currentColor" />
      <circle cx="23" cy="13" r="1" fill="currentColor" />
      <path d="M18 17 Q20 18 22 17" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  ),
  relationship: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="14" cy="14" r="5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="26" cy="14" r="5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M10 28 Q14 22 20 26 Q26 22 30 28" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M20 19 V26" stroke="currentColor" strokeWidth="1" />
    </svg>
  ),
  confusion: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 20 Q12 14 20 20 Q28 26 34 20" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M8 26 Q14 20 22 26 Q30 32 36 26" stroke="currentColor" strokeWidth="1" fill="none" />
      <circle cx="20" cy="10" r="2" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  ),
  health: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 34 Q8 26 8 16 Q8 10 14 10 Q18 10 20 14 Q22 10 26 10 Q32 10 32 16 Q32 26 20 34" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M18 20 Q20 22 22 20" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  ),
  love: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 8 Q28 4 32 12 Q36 20 20 34 Q4 20 8 12 Q12 4 20 8" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M16 16 Q20 18 24 16" stroke="currentColor" strokeWidth="1" fill="none" />
      <path d="M18 22 Q20 24 22 22" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  ),
  finance: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="14" r="8" stroke="currentColor" strokeWidth="1.2" />
      <path d="M20 6 L20 10" stroke="currentColor" strokeWidth="1" />
      <line x1="16" y1="14" x2="24" y2="14" stroke="currentColor" strokeWidth="1" />
      <path d="M10 32 Q16 26 20 30 Q24 26 30 32" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <rect x="14" y="32" width="12" height="2" rx="1" stroke="currentColor" strokeWidth="0.8" fill="none" />
    </svg>
  ),
  family: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 34 L8 24 Q8 20 12 20 L20 20 L28 20 Q32 20 32 24 L32 34" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <line x1="6" y1="34" x2="34" y2="34" stroke="currentColor" strokeWidth="1" />
      <circle cx="14" cy="14" r="3" stroke="currentColor" strokeWidth="1" fill="none" />
      <circle cx="26" cy="14" r="3" stroke="currentColor" strokeWidth="1" fill="none" />
      <path d="M20 8 L20 16" stroke="currentColor" strokeWidth="1" />
      <path d="M16 11 L24 11" stroke="currentColor" strokeWidth="1" />
    </svg>
  ),
};

const categoryColors: Record<string, { top: string; bg: string; hoverBg: string; iconBg: string; iconText: string; activeBg: string; activeBorder: string }> = {
  career: {
    top: 'bg-primary-400/70',
    bg: 'bg-primary-50/40',
    hoverBg: 'group-hover:bg-primary-50/70',
    iconBg: 'bg-primary-100/60',
    iconText: 'text-primary-600',
    activeBg: 'active:bg-primary-100/70',
    activeBorder: 'active:border-primary-400/60',
  },
  parenting: {
    top: 'bg-accent-400/70',
    bg: 'bg-accent-50/40',
    hoverBg: 'group-hover:bg-accent-50/70',
    iconBg: 'bg-accent-100/60',
    iconText: 'text-accent-600',
    activeBg: 'active:bg-accent-100/70',
    activeBorder: 'active:border-accent-400/60',
  },
  relationship: {
    top: 'bg-secondary-400/70',
    bg: 'bg-secondary-50/40',
    hoverBg: 'group-hover:bg-secondary-50/70',
    iconBg: 'bg-secondary-100/60',
    iconText: 'text-secondary-600',
    activeBg: 'active:bg-secondary-100/70',
    activeBorder: 'active:border-secondary-400/60',
  },
  confusion: {
    top: 'bg-primary-300/60',
    bg: 'bg-background-100/60',
    hoverBg: 'group-hover:bg-primary-50/50',
    iconBg: 'bg-primary-50/70',
    iconText: 'text-primary-500',
    activeBg: 'active:bg-primary-100/60',
    activeBorder: 'active:border-primary-300/60',
  },
  health: {
    top: 'bg-accent-300/60',
    bg: 'bg-accent-50/30',
    hoverBg: 'group-hover:bg-accent-50/60',
    iconBg: 'bg-accent-50/70',
    iconText: 'text-accent-500',
    activeBg: 'active:bg-accent-100/60',
    activeBorder: 'active:border-accent-300/60',
  },
  love: {
    top: 'bg-secondary-300/60',
    bg: 'bg-secondary-50/30',
    hoverBg: 'group-hover:bg-secondary-50/60',
    iconBg: 'bg-secondary-50/70',
    iconText: 'text-secondary-500',
    activeBg: 'active:bg-secondary-100/60',
    activeBorder: 'active:border-secondary-300/60',
  },
  finance: {
    top: 'bg-primary-400/70',
    bg: 'bg-primary-50/30',
    hoverBg: 'group-hover:bg-primary-50/60',
    iconBg: 'bg-primary-50/70',
    iconText: 'text-primary-500',
    activeBg: 'active:bg-primary-100/60',
    activeBorder: 'active:border-primary-300/60',
  },
  family: {
    top: 'bg-accent-400/70',
    bg: 'bg-accent-50/30',
    hoverBg: 'group-hover:bg-accent-50/60',
    iconBg: 'bg-accent-50/70',
    iconText: 'text-accent-500',
    activeBg: 'active:bg-accent-100/60',
    activeBorder: 'active:border-accent-300/60',
  },
};

export default function DailyWisdom() {
  const navigate = useNavigate();
  const [selectedProblem, setSelectedProblem] = useState<string | null>(null);
  const [card, setCard] = useState<WisdomCard | null>(null);
  const [drawnToday, setDrawnToday] = useState<number[]>(() => {
    const stored = loadDrawnIds();
    const today = getTodayKey();
    return stored.date === today ? stored.ids : [];
  });
  const [revealing, setRevealing] = useState(false);

  const handleSelectProblem = useCallback(
    (categoryKey: string) => {
      setSelectedProblem(categoryKey);
      setRevealing(true);

      window.scrollTo({ top: 0, behavior: 'smooth' });

      const today = getTodayKey();
      const stored = loadDrawnIds();
      const usedIds = stored.date === today ? stored.ids : [];

      const pool = wisdomCards.filter((w) => w.category === categoryKey && !usedIds.includes(w.id));

      let picked: WisdomCard;
      if (pool.length > 0) {
        picked = pool[Math.floor(Math.random() * pool.length)];
      } else {
        const allPool = wisdomCards.filter((w) => w.category === categoryKey);
        picked = allPool[Math.floor(Math.random() * allPool.length)];
      }

      setTimeout(() => {
        setCard(picked);
        setRevealing(false);
        const newIds = stored.date === today ? [...usedIds, picked.id] : [picked.id];
        setDrawnToday(newIds);
        saveDrawnIds(today, newIds);
      }, 700);
    },
    [],
  );

  const handleReset = () => {
    setCard(null);
    setSelectedProblem(null);
  };

  const selectedCategoryLabel = selectedProblem
    ? problemCategories.find((p) => p.key === selectedProblem)?.label
    : null;

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Navbar />
      <main>
        <section className="relative py-20 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <button
              type="button"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-sm font-medium text-foreground-600 transition-colors cursor-pointer whitespace-nowrap mb-10"
            >
              <i className="ri-arrow-left-line"></i>
              返回上頁
            </button>

            <div className="text-center max-w-2xl mx-auto mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100/80 text-accent-800 text-xs font-semibold tracking-widest uppercase mb-6 whitespace-nowrap">
                <i className="ri-sparkling-line"></i>
                Daily Insight · 每日智慧
              </div>
              <h2 className="font-heading text-3xl md:text-5xl font-medium text-foreground-950 leading-tight">
                生活有啲攰？入嚟抱抱自己
              </h2>
            </div>

            {!selectedProblem ? (
              <div>
                {/* Warm guidance text */}
                <div className="max-w-md mx-auto mb-10 text-center">
                  <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                    慢慢嚟，唔使急。喺呢度，你可以安心咁揀一個最貼近你而家狀態嘅生命課題。
                  </p>
                  <p className="mt-2 text-sm text-foreground-400 leading-relaxed">
                    每一個壓力都值得被看見、被承接。揀完之後，抽一張屬於你今日嘅心靈指引。
                  </p>
                </div>

                {/* Category selection - elegant cards with line-art */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-lg mx-auto">
                  {problemCategories.map((p) => {
                    const today = getTodayKey();
                    const stored = loadDrawnIds();
                    const usedIds = stored.date === today ? stored.ids : [];
                    const remaining = wisdomCards.filter((w) => w.category === p.key && !usedIds.includes(w.id));
                    const colors = categoryColors[p.key];
                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => handleSelectProblem(p.key)}
                        className={`group relative flex flex-col items-center gap-3 px-4 py-6 rounded-2xl border border-background-200/50 transition-all duration-300 hover:scale-[1.03] hover:shadow-sm ${colors.bg} ${colors.hoverBg} ${colors.activeBg} ${colors.activeBorder} cursor-pointer`}
                      >
                        {/* Top accent line */}
                        <div className={`absolute top-0 left-4 right-4 h-[2px] rounded-full ${colors.top}`}></div>

                        {/* Line-art icon in circular bg */}
                        <div className={`w-12 h-12 md:w-14 md:h-14 rounded-full ${colors.iconBg} ${colors.iconText} flex items-center justify-center transition-transform duration-300 group-hover:scale-110`}>
                          <div className="w-7 h-7 md:w-8 md:h-8">
                            {categoryArts[p.key]}
                          </div>
                        </div>

                        <span className="text-sm font-semibold text-foreground-800 whitespace-nowrap transition-colors">
                          {p.label}
                        </span>
                        <span className="text-[11px] text-foreground-400">
                          {remaining.length > 0 ? `${remaining.length} 張智慧卡` : '今日已抽晒'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : revealing ? (
              /* Loading state */
              <div className="flex flex-col items-center py-16">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-100 animate-pulse">
                  <i className="ri-leaf-line text-3xl text-accent-500"></i>
                </div>
                <p className="mt-5 text-sm text-foreground-500">抽取緊今日智慧...</p>
              </div>
            ) : card ? (
              /* Card result - light background, refined design */
              <div className="max-w-md mx-auto animate-[fadeIn_0.6s_ease-out]">
                <div className="rounded-3xl bg-background-100 border border-background-200/70 p-8 md:p-10">
                  {/* Date and category */}
                  <div className="flex items-center justify-between text-xs mb-8">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-50 text-accent-700 font-medium whitespace-nowrap">
                      <div className="w-4 h-4 flex items-center justify-center text-accent-600">
                        {selectedProblem ? categoryArts[selectedProblem] : null}
                      </div>
                      {selectedCategoryLabel}
                    </span>
                    <span className="tracking-[0.15em] text-foreground-400">
                      {getTodayKey()}
                    </span>
                  </div>

                  {/* Card content */}
                  <div className="flex flex-col items-center text-center">
                    {/* Keyword - refined with Japanese line frame, smaller, no italic */}
                    <div className="relative inline-flex items-center justify-center">
                      {/* Line frame decoration */}
                      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                        <rect x="4" y="4" width="152" height="152" rx="16" stroke="currentColor" strokeWidth="1" className="text-primary-300/50" fill="none" />
                        <path d="M4 44 L24 44" stroke="currentColor" strokeWidth="1.2" className="text-primary-400/40" fill="none" />
                        <path d="M136 44 L156 44" stroke="currentColor" strokeWidth="1.2" className="text-primary-400/40" fill="none" />
                        <path d="M4 116 L24 116" stroke="currentColor" strokeWidth="1.2" className="text-primary-400/40" fill="none" />
                        <path d="M136 116 L156 116" stroke="currentColor" strokeWidth="1.2" className="text-primary-400/40" fill="none" />
                      </svg>
                      <span
                        className="relative z-10 font-heading text-5xl md:text-6xl font-medium text-primary-600 leading-none py-8 px-10"
                        style={{ fontFamily: "'M PLUS Rounded 1c', 'Fraunces', serif" }}
                      >
                        {card.keyword}
                      </span>
                    </div>

                    {/* Title */}
                    <p
                      className="mt-5 text-sm md:text-base font-medium text-foreground-700 leading-relaxed"
                      style={{ fontFamily: "'M PLUS Rounded 1c', 'Noto Sans TC', sans-serif" }}
                    >
                      今日宇宙想同你講...
                    </p>

                    {/* Message */}
                    <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-800 max-w-sm">
                      「{card.message}」
                    </p>

                    {/* Practice */}
                    <div className="mt-6 w-full rounded-2xl bg-secondary-50 border border-secondary-100/70 p-5 text-left">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-secondary-600 mb-1.5 font-semibold">
                        <i className="ri-lightbulb-flash-line mr-1"></i>今日小練習
                      </div>
                      <p className="text-sm text-foreground-800 leading-relaxed">{card.practice}</p>
                    </div>
                  </div>

                  {/* Actions - 2 dedicated share buttons */}
                  <div className="mt-7 flex flex-col items-center gap-3">
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <IgStoryShare card={card} />
                    </div>
                    <button
                      type="button"
                      onClick={handleReset}
                      className="px-4 py-2 rounded-full text-sm font-medium text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                    >
                      <i className="ri-arrow-go-back-line"></i>
                      重新選擇
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}